import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { X } from "lucide-react";
import { clock, stationLabel } from "../../core/format";
import { pickupTiming, planningCountdown, planningNote, planningStandings, PLANNING_STANDING_LABELS, PLANNING_STANDING_TONES, type PlanningStanding } from "../../core/pickup";
import { SHUTTLE_STOP } from "../../core/shuttle-stop";
import { distanceMeters } from "../../shared/time";
import type { CommutePlan, CommuteSnapshot } from "../../shared/types";
import { ServiceHealthCard, TrainBoard, UpstreamTrace } from "./Details";
import { NavigateLinks } from "./NavigateLinks";
import { WhatsNew } from "./WhatsNew";

const PICKUP_PREP_SECONDS = 5 * 60;

function minutesUntil(ms: number, nowMs: number): number {
	return Math.max(0, Math.round((ms - nowMs) / 60_000));
}

/** David's private way into scheduled pickup browsing. */
export function PickupPlanningButton({ onClick }: { onClick: () => void }) {
	return (
		<button type="button" className="planning__entry" onClick={onClick}>
			Pickup planning
		</button>
	);
}

/** David-only scheduled browsing. It is never a pickup, a message, or an alert. */
export function PickupPlanning({
	snapshot,
	nowMs,
	originLabel,
	cutoffMs,
	onShowNotices,
	riderStatus,
	maps,
	diagnostics,
	onClose,
	selectedId,
	onSelect,
}: {
	snapshot: CommuteSnapshot;
	nowMs: number;
	originLabel: string;
	/** The dynamic cutoff the Worker uses: later of the daytime floor and two hours from now. */
	cutoffMs: number;
	onShowNotices: () => void;
	riderStatus: ReactNode;
	maps?: ReactNode;
	diagnostics: ReactNode;
	onClose: () => void;
	selectedId: string | null;
	onSelect: (planId: string | null) => void;
}) {
	// The boundary card is a place the thumb can rest, not a train, so it moves the dots without
	// moving the selection the detail panel below is about.
	const [atBoundary, setAtBoundary] = useState(false);
	const scrollerRef = useRef<HTMLDivElement>(null);
	const settleRef = useRef<number | undefined>(undefined);
	const programmaticRef = useRef(false);
	const selectedIndex = Math.max(0, snapshot.options.findIndex((plan) => plan.id === selectedId));
	const untilLabel = clock(cutoffMs, snapshot.timezone);

	useEffect(() => {
		if (!snapshot.options.some((plan) => plan.id === selectedId)) {
			onSelect(snapshot.options[0]?.id ?? null);
		}
	}, [snapshot.options, selectedId, onSelect]);

	useLayoutEffect(() => {
		const scroller = scrollerRef.current;
		const card = scroller?.children[selectedIndex] as HTMLElement | undefined;
		if (selectedId == null || !scroller || !card || Math.abs(scroller.scrollLeft - card.offsetLeft) < 4) {
			return;
		}
		programmaticRef.current = true;
		scroller.scrollTo({ left: card.offsetLeft, behavior: "auto" });
		window.setTimeout(() => {
			programmaticRef.current = false;
		}, 120);
	}, [selectedIndex]);

	useEffect(() => () => window.clearTimeout(settleRef.current), []);
	/*
	 * Planning happens while he is still at school, so that is where the deck is read from. The
	 * boarding station comes off the snapshot rather than being named here, so this follows the
	 * schedule if the afternoon ever starts somewhere other than Rockridge.
	 */
	const standings = useMemo(
		() =>
			planningStandings(
				snapshot.options.map((plan) => ({ id: plan.id, departureMs: plan.boarding.predictedMs })),
				distanceMeters(SHUTTLE_STOP, snapshot.profile.boardingStation),
				nowMs,
			),
		[snapshot.options, snapshot.profile.boardingStation, nowMs],
	);
	const heading = (
		<header className="planning__head">
			<div>
				<p className="planning__eyebrow">Private</p>
				<h2>Pickup planning</h2>
			</div>
			<button type="button" className="planning__close" onClick={onClose} aria-label="Close pickup planning" title="Close pickup planning">
				<X size={20} strokeWidth={2} aria-hidden="true" />
			</button>
		</header>
	);

	return (
		<section className="planning" aria-label="Pickup planning">
			{heading}

			<div
				className="deck__scroller"
				ref={scrollerRef}
				onScroll={() => {
					if (programmaticRef.current) return;
					window.clearTimeout(settleRef.current);
					settleRef.current = window.setTimeout(() => {
						const scroller = scrollerRef.current;
						if (!scroller) return;
						const cards = [...scroller.children] as HTMLElement[];
						const centre = scroller.scrollLeft + scroller.clientWidth / 2;
						const nearest = cards.reduce(
							(best, card, index) => {
								const distance = Math.abs(card.offsetLeft + card.clientWidth / 2 - centre);
								return distance < best.distance ? { index, distance } : best;
							},
							{ index: 0, distance: Number.POSITIVE_INFINITY },
						);
						if (nearest.index >= snapshot.options.length) {
							setAtBoundary(true);
							return;
						}
						setAtBoundary(false);
						onSelect(snapshot.options[nearest.index]?.id ?? null);
					}, 90);
				}}
			>
				{snapshot.options.map((plan) => (
					<PlanningCard key={plan.id} plan={plan} snapshot={snapshot} nowMs={nowMs} originLabel={originLabel} standing={standings[plan.id]} />
				))}
				<article className="card card--pickup card--planning planning__boundary" aria-label="End of pickup planning trains">
					<p className="card__lead">LATER TRAINS NOT SHOWN</p>
					<p className="planning__boundaryText">Pickup Planning shows departures through {untilLabel}.</p>
				</article>
			</div>
			{/* One dot per card, boundary included, or the last swipe leaves the lit dot behind. */}
			<div className="deck__dots" aria-hidden="true">
				{snapshot.options.map((plan, index) => (
					<span key={plan.id} className={`deck__dot ${!atBoundary && index === selectedIndex ? "is-active" : ""}`} />
				))}
				<span className={`deck__dot ${atBoundary ? "is-active" : ""}`} />
			</div>
			{riderStatus}
			{maps}

			{snapshot.options.length > 0 ? (
				<div className="app__detail planning__detail">
					<ServiceHealthCard snapshot={snapshot} onShowNotices={onShowNotices} />
					<TrainBoard
						snapshot={snapshot}
						selectedId={selectedId}
						mode="onboard"
						nowMs={nowMs}
						destinationIndex={0}
						showBoardingTime
						onSelect={onSelect}
					/>
					<UpstreamTrace
						plan={snapshot.options.find((plan) => plan.id === selectedId) ?? null}
						snapshot={snapshot}
						nowMs={nowMs}
						onboard
						destinationStopId={snapshot.profile.destinations[0]?.id ?? null}
					/>
					<WhatsNew />
					{diagnostics}
				</div>
			) : null}
		</section>
	);
}

function PlanningCard({
	plan,
	snapshot,
	nowMs,
	originLabel,
	standing,
}: {
	plan: CommutePlan;
	snapshot: CommuteSnapshot;
	nowMs: number;
	originLabel: string;
	/** Whether Eben could be on this one, from school. */
	standing: PlanningStanding | undefined;
}) {
	const arrival = plan.destinations[0] ?? null;
	const timing = pickupTiming({
		trainArrivalMs: arrival?.predictedMs ?? null,
		driveSeconds: snapshot.drive?.durationSeconds ?? null,
		prepSeconds: PICKUP_PREP_SECONDS,
		driverArrivalMs: null,
		nowMs,
	});
	const leaveIn = timing.startByMs == null ? null : minutesUntil(timing.startByMs, nowMs);
	const countdown = leaveIn == null ? null : planningCountdown(leaveIn);
	const station = stationLabel(arrival?.stopName ?? "Civic Center");
	const driveMinutes = snapshot.drive == null ? null : Math.round(snapshot.drive.durationSeconds / 60);
	const tone = leaveIn == null ? "info" : leaveIn <= 0 ? "bad" : leaveIn <= 10 ? "warn" : "good";
	const leaveAt = timing.startByMs == null ? null : clock(timing.startByMs, snapshot.timezone);
	const driveTarget = snapshot.profile.destinations[0] ?? { id: "CIVC", name: "Civic Center", lat: 37.779732, lon: -122.414123 };
	const note = planningNote(timing, nowMs);
	const departureIn = Math.round((plan.boarding.predictedMs - nowMs) / 60_000);
	const arrivalIn = arrival == null ? null : minutesUntil(arrival.predictedMs, nowMs);

	return (
		<article className={`card card--pickup card--planning tone-${tone}`}>
			<header className="card__tag">
				<span className="card__tag">Scheduled</span>
				{standing ? <span className={`pill pill--${PLANNING_STANDING_TONES[standing]}`}>{PLANNING_STANDING_LABELS[standing]}</span> : null}
			</header>
			<div className="card__hero">
				<p className="card__lead">{leaveIn == null ? "PICKUP" : leaveIn === 0 ? "LEAVE NOW" : "LEAVE IN"}</p>
				<div className="card__valueRow">
					<p className={`card__value${leaveIn != null && leaveIn >= 60 ? " planning__duration" : ""}`}>
						{leaveIn == null ? "—" : leaveIn === 0 ? "NOW" : <span>{countdown!.value}<span className="card__unit">{countdown!.unit}</span></span>}
						{countdown?.remainder ? <span className="planning__remainder"> {countdown.remainder}</span> : null}
					</p>
					<NavigateLinks target={driveTarget} label={`Drive to ${station} pickup`} />
				</div>
				<div className="card__actionRow">
					<p className="planning__departure">{leaveAt ? <>at <strong>{leaveAt}</strong> from {originLabel}</> : "Drive estimate unavailable"}</p>
					{note ? (
						<p className={`card__note card__note--${note.tone}`}>
							<span className="card__noteValue">{note.value}</span> {note.label}
						</p>
					) : null}
					<p className="planning__scenario">Rockridge at {clock(plan.boarding.predictedMs, snapshot.timezone)}</p>
				</div>
			</div>
			<dl className="card__facts">
				<div>
					<dt>Rockridge</dt>
					<dd>{departureIn}<span className="card__factUnit">min</span></dd>
				</div>
				<div>
					<dt>{station}</dt>
					<dd>{arrivalIn ?? "—"}{arrivalIn != null ? <span className="card__factUnit">min</span> : null}</dd>
				</div>
				<div>
					<dt>Your drive</dt>
					<dd>{driveMinutes ?? "—"}{driveMinutes != null ? <span className="card__factUnit">min</span> : null}</dd>
				</div>
			</dl>
		</article>
	);
}
